import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input } from '../../components/ui';

interface AssignedVendor {
  id: string;
  name: string;
  website: string;
  programName: string;
  programId: string;
  assignedDate: string;
  reviewsCompleted: number;
  lastReviewDate?: string;
}

const mockVendors: AssignedVendor[] = [
  {
    id: '1',
    name: 'SkyReach Dynamics',
    website: 'skyreachdynamics.com',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    assignedDate: 'Oct 15, 2025',
    reviewsCompleted: 2,
    lastReviewDate: 'Oct 22, 2025',
  },
  {
    id: '2',
    name: 'Raptor Aerospace',
    website: 'raptoraero.com',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    assignedDate: 'Oct 16, 2025',
    reviewsCompleted: 1,
    lastReviewDate: 'Oct 18, 2025',
  },
  {
    id: '5',
    name: 'WingTech Systems',
    website: 'wingtechsys.com',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    assignedDate: 'Oct 14, 2025',
    reviewsCompleted: 3,
    lastReviewDate: 'Oct 23, 2025',
  },
  {
    id: '8',
    name: 'Horizon Drones',
    website: 'horizondrones.io',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    assignedDate: 'Oct 17, 2025',
    reviewsCompleted: 2,
    lastReviewDate: 'Oct 21, 2025',
  },
  {
    id: '6',
    name: 'AeroSentry',
    website: 'aerosentry.io',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    assignedDate: 'Oct 13, 2025',
    reviewsCompleted: 2,
    lastReviewDate: 'Oct 20, 2025',
  },
  {
    id: '12',
    name: 'Delta Wing Co',
    website: 'deltawingco.com',
    programName: 'RFP-2024-003: Hypersonic Defense',
    programId: '4',
    assignedDate: 'Sep 28, 2025',
    reviewsCompleted: 4,
    lastReviewDate: 'Oct 19, 2025',
  },
  {
    id: '14',
    name: 'Falcon Unmanned',
    website: 'falconunmanned.com',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    assignedDate: 'Oct 10, 2025',
    reviewsCompleted: 3,
    lastReviewDate: 'Oct 24, 2025',
  },
  {
    id: '7',
    name: 'Stratosphere UAV',
    website: 'stratosphereuav.com',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    assignedDate: 'Oct 12, 2025',
    reviewsCompleted: 3,
    lastReviewDate: 'Oct 23, 2025',
  },
];

const ReviewerVendors = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredVendors = mockVendors.filter(
    (vendor) =>
      vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.website.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.programName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group by program
  const vendorsByProgram = filteredVendors.reduce((acc, vendor) => {
    if (!acc[vendor.programName]) {
      acc[vendor.programName] = [];
    }
    acc[vendor.programName].push(vendor);
    return acc;
  }, {} as Record<string, AssignedVendor[]>);

  const handleVendorClick = (vendor: AssignedVendor) => {
    // Navigate to vendor detail page in reviewer context
    navigate(`/review/vendors/${vendor.id}`);
  };

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground">My Vendors</h1>
          <p className="text-base text-muted-foreground mt-2">
            Vendors you've been assigned to review across all programs
          </p>
        </div>

        {/* Search */}
        <div className="bg-white dark:bg-slate-900 border border-border rounded-lg p-4 mb-6">
          <Input
            placeholder="Search vendors or programs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Total Assigned Vendors</p>
            <p className="text-3xl font-bold text-foreground">
              {mockVendors.length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Reviews Completed</p>
            <p className="text-3xl font-bold text-foreground">
              {mockVendors.reduce((sum, v) => sum + v.reviewsCompleted, 0)}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Active Programs</p>
            <p className="text-3xl font-bold text-foreground">
              {Object.keys(vendorsByProgram).length}
            </p>
          </Card>
        </div>

        {/* Vendors List - Grouped by Program */}
        <div className="space-y-8">
          {Object.entries(vendorsByProgram).map(([programName, vendors]) => (
            <div key={programName}>
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {programName}
                <span className="text-sm text-muted-foreground font-normal">
                  ({vendors.length} vendors)
                </span>
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {vendors.map((vendor) => (
                  <Card
                    key={vendor.id}
                    className="p-5 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-500 dark:hover:border-blue-500"
                    onClick={() => handleVendorClick(vendor)}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-foreground truncate">
                          {vendor.name}
                        </h3>
                        <p className="text-sm text-muted-foreground truncate">
                          {vendor.website}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Assigned
                        </p>
                        <p className="text-sm font-medium text-foreground">
                          {vendor.assignedDate}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Reviews Completed
                        </p>
                        <p className="text-sm font-medium text-foreground">
                          {vendor.reviewsCompleted}
                        </p>
                      </div>
                    </div>

                    {vendor.lastReviewDate && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                          Last reviewed: <span className="text-foreground">{vendor.lastReviewDate}</span>
                        </p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}

          {filteredVendors.length === 0 && (
            <Card className="p-12 text-center">
              <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-lg font-semibold text-foreground mb-2">
                No vendors found
              </p>
              <p className="text-muted-foreground">
                Try adjusting your search criteria
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewerVendors;

